import React, {Fragment, useContext, useEffect, useRef, useState} from "react";
import {isEmpty} from "../Utils";
import {useSelector} from "react-redux";
import {MyContext} from "../../contexts/MyContext";
import moment from 'moment-timezone';
import 'moment/locale/fr';
import 'moment/locale/en-gb';

const Chat = ({live, playerClass}) => {
    const translations = useSelector(
        (state) => state.langReducer.translationsReducer.translate
    );
    const messagesEndRef = useRef(null);
    const [message, setMessage] = useState("");
    const [messages, setMessages] = useState([]);
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const {rootState} = useContext(MyContext);
    const {isAuth, theUser} = rootState;

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({behavior: "smooth"});
        }
    };
    const formatTime = (time, translations) => {
        const lang = (translations["l"] || 'fr').toLowerCase();
        const locale = lang === 'fr' ? 'fr' : 'en-gb';
        moment.locale(locale);

        const nowParis = moment().tz('Europe/Paris');
        
        // ignoram Z și interpretăm stringul ca Europe/Paris
        const localTime = moment.tz(time.replace(/Z$/, ''), 'YYYY-MM-DDTHH:mm:ss', 'Europe/Paris');

        const diffInHours = nowParis.diff(localTime, 'hours');

        if (diffInHours < 24) {
            return localTime.from(nowParis);
        } else {
            return localTime.format('DD-MM HH:mm');
        }
    };


    useEffect(() => {
        if (live?.chat !== 'yes') {
            console.log("Chat is disabled for this live event.");
            return;
        }
        // ws conn

        const ws = new WebSocket(`wss://wchat.novaservice.link/chat?room=${live.id}&username=${isAuth ? theUser.username : "Guest"}&code=${live?.code || ""}`);
        setSocket(ws);
        ws.onopen = () => {
            setIsConnected(true);

            // Send request to recover start msgs
            ws.send(
                JSON.stringify({
                    type: "getMessages",
                    lastMessageId: 0, // recover 15 msgs
                    eventId: live.id,
                })
            );
        };
        ws.onclose = () => {
            setIsConnected(false); // Update state when connection is closed
        };

        ws.onerror = (error) => {
            console.error("WebSocket error:", error);
            setIsConnected(false); // Handle errors
        };
        // msg recv from srv
        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);

            if (data.type === "newMessage") {
                // add msg to messages
                setMessages((prevMessages) => [...prevMessages, data.data]);
            } else if (data.type === "messageList") {
                // load current messages
                setMessages(data.data);
            } else if (data.type === "messageDeleted") {
                // remove msg from current list.
                const messageId = parseInt(data.messageId, 10);
                console.log("Deleting message with ID:", messageId);
                setMessages((prevMessages) => {
                    const filteredMessages = prevMessages.filter((message) => message.m_id !== messageId);
                    console.log("Filtered messages:", filteredMessages);
                    return filteredMessages;
                });
            }
        };

        // cleanup
        return () => {
            ws.close();
        };
    }, [live.id, isAuth, theUser?.username, live?.chat, live?.code]);

    // send msg trough ws
    const sendMessage = () => {
        if (!isAuth) {
            alert((translations && !isEmpty(translations["chat_logintosendmsg"]) ? translations["chat_logintosendmsg"] : "You must be logged in to send messages."));
            return;
        }

        if (socket && message.trim() && playerClass) {
            const videoTime = playerClass.currentTime(); // get time from player
            socket.send(
                JSON.stringify({
                    type: "sendMessage",
                    m_evt: live.id,
                    m_uid: rootState?.theUser?.id || 0,
                    m_username: rootState?.theUser?.username || "Guest",
                    m_time: videoTime.toFixed(2), // current live time with 2 decimals..
                    m_msg: message,
                })
            );
            setMessage(""); // reset input field
        }
    };

    const toggleChat = () => {
        setIsChatOpen((prev) => !prev);
        setTimeout(() => scrollToBottom(), 0);
    };

    return (
        <Fragment>
            {live?.chat === 'yes' && (
                <button className="button button-blue shared" id="chat_btn" onClick={toggleChat}>
                    <span>
                    {translations && !isEmpty(translations["view_chat"])
                        ? translations["view_chat"]
                        : "View chat"}
                    </span>
                    <svg width="30px" height="30px" viewBox="0 0 24.00 24.00" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#fafafa" transform="rotate(0)matrix(1, 0, 0, 1, 0, 0)">
                        <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                        <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                        <g id="SVGRepo_iconCarrier">
                            <path d="M17 3.33782C15.5291 2.48697 13.8214 2 12 2C6.47715 2 2 6.47715 2 12C2 13.5997 2.37562 15.1116 3.04346 16.4525C3.22094 16.8088 3.28001 17.2161 3.17712 17.6006L2.58151 19.8267C2.32295 20.793 3.20701 21.677 4.17335 21.4185L6.39939 20.8229C6.78393 20.72 7.19121 20.7791 7.54753 20.9565C8.88837 21.6244 10.4003 22 12 22C17.5228 22 22 17.5228 22 12C22 10.1786 21.513 8.47087 20.6622 7" stroke="#ffffff" strokeWidth="1.656" strokeLinecap="round"></path>
                            <path d="M8 12H8.009M11.991 12H12M15.991 12H16" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"></path>
                        </g>
                    </svg>
                </button>)}
            {isChatOpen && live?.chat === 'yes' && (
                <div className="chat-overlay">
                    <div className="chat-container">
                        <button className="chat-close-btn" onClick={toggleChat}>
                            X
                        </button>
                        <div className="messages">
                            {messages.map((msg, index) => (
                                <div key={index} className="message">
                                    <strong>{msg.m_username}</strong> {msg.m_msg}
                                    <span className="time"> {formatTime(msg.m_added, translations)}</span>
                                </div>
                            ))}
                            <div id="messages-end" ref={messagesEndRef}/>
                        </div>
                        <div className="input-container">
                            <textarea
                                value={message}
                                className={!isAuth ? "dark-placeholder" : ""}
                                onChange={(e) => setMessage(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        sendMessage();
                                    }
                                }}
                                placeholder={isAuth ? (translations && !isEmpty(translations["chat_writemsg"]) ? translations["chat_writemsg"] : "Write your message...") : (translations && !isEmpty(translations["chat_logintomsg"]) ? translations["chat_logintomsg"] : "Login to send messages")}
                                disabled={!isAuth || !isConnected}
                            />
                            <div className="action-container">
                                <div className={`char-counter ${200 - message.length < 0 ? 'negative' : 'positive'}`}>
                                    {message.length >= 33
                                        ? `${200 - message.length}`
                                        : ""}
                                </div>
                                <div className="send-icon" title="Send" onClick={() => {
                                    if (message.trim().length > 0 && message.length <= 200) {
                                        sendMessage();
                                    }
                                }}>
                                    <svg
                                        width="25px"
                                        height="25px"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M7.39999 6.32003L15.89 3.49003C19.7 2.22003 21.77 4.30003 20.51 8.11003L17.68 16.6C15.78 22.31 12.66 22.31 10.76 16.6L9.91999 14.08L7.39999 13.24C1.68999 11.34 1.68999 8.23003 7.39999 6.32003Z"
                                            stroke="#222"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M10.11 13.6501L13.69 10.0601"
                                            stroke="#222"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    )
}

export default Chat